import { createContext } from 'react'
import type {
  Customer,
  CustomerFavoriteProduct,
  CustomerInsights,
  CustomerOrder,
  CustomerStoreType,
  CustomerTimelineItem,
} from '../../../types/customer'
import type { CustomerAdapter } from '../adapters/types'

// Snapshot of per-customer data passed down to the profile UI. The provider
// orchestrates a handful of parallel queries (detail + insights + timeline
// + loyalty logs + orders) and exposes them through a single context value
// so that downstream tabs/sidebar cards can read what they need without
// rewriting the fetch wiring.
//
// Patterns mirror `ProductDetailProvider` (`features/Product/context/`).

// Projection of an orders infinite-query result. Both the Orders sub-tabs
// (Online / POS) and the Purchase Channels sidebar card read the same
// channel object so the underlying HTTP request fires once per customer.
export interface CustomerOrdersChannel {
  // True when the channel is fetchable for this customer:
  //   • ecom  → always true — filters by wooCustomerId when linked, falls
  //             back to the customer's POS id otherwise
  //   • pos   → adapter exposes `listPosOrders`
  isAvailable: boolean
  // Flattened rows across all loaded pages.
  orders: CustomerOrder[]
  // Total available rows reported by page 1 of the response.
  totalItems: number
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  hasMore: boolean
  isFetchingMore: boolean
  fetchNextPage: () => void
}

// Active-tab identity for the profile's main card. Shared between the route
// wrapper (which serialises it to `?tab=`) and `CustomerProfileTabs` (which
// renders + switches) so the query-string contract has a single source of
// truth. `orders` is the default tab and maps to a bare URL (no `tab` param).
export type CustomerProfileTabKey =
  | 'orders'
  | 'products'
  | 'loyalty'
  | 'behavior'

export const CUSTOMER_PROFILE_TAB_KEYS: CustomerProfileTabKey[] = [
  'orders',
  'products',
  'loyalty',
  'behavior',
]

export const DEFAULT_CUSTOMER_PROFILE_TAB: CustomerProfileTabKey = 'orders'

// Active sub-tab of the extended (flagged) Behavior tab. Serialised to
// `?subtab=` by the route wrapper so a reload keeps the sub-tab; `overview` is
// the default and maps to a bare URL (no `subtab` param).
export type CustomerBehaviorSubTabKey =
  | 'overview'
  | 'engagement'
  | 'search'
  | 'products'
  | 'cart'

export const CUSTOMER_BEHAVIOR_SUB_TAB_KEYS: CustomerBehaviorSubTabKey[] = [
  'overview',
  'engagement',
  'search',
  'products',
  'cart',
]

export const DEFAULT_CUSTOMER_BEHAVIOR_SUB_TAB: CustomerBehaviorSubTabKey =
  'overview'

export interface CustomerDetailContextValue {
  // Identity
  storeId: string
  storeType: CustomerStoreType
  adapter: CustomerAdapter
  customerId: string

  // Primary customer record
  customer: Customer | undefined
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  error: unknown

  // Optional sub-resources. Each one resolves independently — the UI should
  // render its own placeholder while the corresponding flag is true.
  insights: CustomerInsights | undefined
  isInsightsLoading: boolean
  isInsightsError: boolean

  timeline: CustomerTimelineItem[]
  isTimelineLoading: boolean

  // Orders channels. Single source of truth for every detail-page consumer
  // that needs an order list or `totalItems` count — fetched once at the
  // provider level instead of once per panel + once per sidebar probe.
  ecomOrders: CustomerOrdersChannel
  posOrders: CustomerOrdersChannel

  // Navigation back to the list. Populated by the route wrapper; defaults to
  // a no-op so the provider is safe outside a router.
  goBack: () => void

  // Row-level navigation helpers populated by the route wrapper. Default to
  // no-ops so the provider remains safe outside a router. Tabs invoke these
  // when an order or favourite-product row is clicked.
  goToOrder: (order: CustomerOrder) => void
  goToProduct: (product: CustomerFavoriteProduct) => void

  // Active tab of the profile's main card. Sourced from `?tab=` by the route
  // wrapper and threaded through the provider so the tab UI stays
  // react-router-free. `setActiveProfileTab` writes the query string.
  activeProfileTab: CustomerProfileTabKey
  setActiveProfileTab: (tab: CustomerProfileTabKey) => void

  // Active sub-tab of the extended Behavior tab, sourced from `?subtab=` the
  // same way as `activeProfileTab`.
  activeBehaviorSubTab: CustomerBehaviorSubTabKey
  setActiveBehaviorSubTab: (subTab: CustomerBehaviorSubTabKey) => void
}

export const CustomerDetailContext = createContext<
  CustomerDetailContextValue | undefined
>(undefined)
